(() => {
  'use strict';

  const script = document.currentScript;
  const stateKey = 'eg-raised-bed-email-offer';
  const stateLifetime = 24 * 60 * 60 * 1000;
  const giftProperty = '_eg_raised_bed_email_offer';
  const cartRoot = window.Shopify?.routes?.root || '/';
  const roots = () => [...document.querySelectorAll('[data-raised-bed-email-offer]')];
  let data;
  let dataPromise;
  const qualifyingProductIds = new Map();
  let syncQueue = Promise.resolve();
  let syncTimer;
  let rejectedGiftFingerprint = '';

  const getState = () => {
    try {
      const saved = sessionStorage.getItem(stateKey) || localStorage.getItem(stateKey);
      const state = JSON.parse(saved);
      return state && typeof state.campaignId === 'string' && Date.now() - state.updatedAt < stateLifetime ? state : null;
    } catch {
      return null;
    }
  };

  const setState = (campaignId, codeSeen = false) => {
    const value = JSON.stringify({ campaignId, codeSeen, updatedAt: Date.now() });
    try {
      sessionStorage.setItem(stateKey, value);
    } catch {}
    try { localStorage.setItem(stateKey, value); } catch {}
  };

  const clearState = () => {
    try { sessionStorage.removeItem(stateKey); } catch {}
    try { localStorage.removeItem(stateKey); } catch {}
    document.querySelector('#eg-bed-offer-bar')?.remove();
    document.body.classList.remove('eg-bed-offer-active');
    document.body.style.removeProperty('--eg-bed-offer-bar-height');
  };

  const getCampaign = () => data?.campaigns?.[getState()?.campaignId] || null;

  const getData = async () => {
    if (!dataPromise) {
      dataPromise = fetch(script.dataset.config)
        .then((response) => {
          if (!response.ok) throw new Error('The offer details could not load. Please refresh and try again.');
          return response.json();
        })
        .then((result) => {
          data = result;
          return result;
        }).catch((error) => {
          dataPromise = null;
          throw error;
        });
    }
    return dataPromise;
  };

  const api = async (path, body) => {
    const response = await fetch(`${cartRoot}${path}`, body === undefined ? { cache: 'no-store' } : {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || 'Your cart could not be updated. Please try again.');
    return result;
  };

  const getQualifyingProductIds = async (campaign) => {
    if (qualifyingProductIds.has(campaign.qualifyingCollectionHandle)) return qualifyingProductIds.get(campaign.qualifyingCollectionHandle);
    const response = await fetch(`${cartRoot}collections/${campaign.qualifyingCollectionHandle}/products.json?limit=250`);
    if (!response.ok) throw new Error('The raised-bed collection could not load. Please refresh and try again.');
    const collection = await response.json();
    const ids = new Set((collection.products || []).map((product) => String(product.id)));
    qualifyingProductIds.set(campaign.qualifyingCollectionHandle, ids);
    return ids;
  };

  const qualifyingBedCount = (cart, productIds) => cart.items.reduce((count, item) => (
    productIds.has(String(item.product_id)) ? count + item.quantity : count
  ), 0);

  const giftItems = (cart, campaignId) => cart.items.filter((item) => (
    item.properties?.[giftProperty] === campaignId
  ));

  const updateGiftItems = async (items, keepFirst) => {
    const updates = {};
    items.forEach((item, index) => {
      updates[item.key] = keepFirst && index === 0 ? 1 : 0;
    });
    return api('cart/update.js', { updates });
  };

  const hasCampaignCode = (cart, campaign) => (cart.discount_codes || []).some((discount) => (
    discount.code?.toUpperCase() === campaign.code.toUpperCase()
  ));

  const applyCampaignCode = (campaign) => api('cart/update.js', { discount: campaign.code });

  const recoverState = (cart) => {
    const current = getState();
    if (current && data.campaigns[current.campaignId]) return current;
    const campaignId = Object.keys(data.campaigns).find((id) => hasCampaignCode(cart, data.campaigns[id]));
    if (campaignId) setState(campaignId, true);
    return getState();
  };

  const offerCodeIsApplicable = (cart, campaign) => {
    const code = (cart.discount_codes || []).find((discount) => (
      discount.code?.toUpperCase() === campaign.code.toUpperCase()
    ));
    return Boolean(code && code.applicable !== false);
  };

  const publishCartUpdate = (cart) => {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'raised-bed-email-offer', cartData: cart });
    }
    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: { source: 'raised-bed-email-offer', cartData: cart },
    }));
  };

  const reconcileCartDrawer = async (cart) => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof window.refreshCartDrawerHotZones !== 'function') return;

    const renderedKeys = new Set([...drawer.querySelectorAll('#CartDrawer-CartItems .cart-item[data-line-key]')]
      .map((item) => item.dataset.lineKey));
    const cartKeys = new Set(cart.items.map((item) => item.key));
    if (renderedKeys.size === cartKeys.size && [...cartKeys].every((key) => renderedKeys.has(key))) return;

    const response = await fetch(`${cartRoot}cart?sections=cart-drawer,cart-icon-bubble`, { cache: 'no-store' });
    if (!response.ok) throw new Error('Your cart drawer could not refresh. Please refresh the page.');
    const sections = await response.json();
    if (!sections['cart-drawer'] || !window.refreshCartDrawerHotZones({ sections, item_count: cart.item_count })) {
      throw new Error('Your cart drawer could not refresh. Please refresh the page.');
    }
  };

  const messageFor = (campaign, bedCount, codeApplicable, cart) => {
    if (campaign.type === 'buy-three-get-one') {
      if (bedCount < 4) return `Add ${4 - bedCount} more qualifying bed${4 - bedCount === 1 ? '' : 's'} to get the lowest-priced bed free.`;
      const freeBeds = Math.floor(bedCount / 4);
      if (!codeApplicable) return 'Your beds qualify, but the free-bed discount is not applying. Check your cart before checkout.';
      return `${freeBeds} bed${freeBeds === 1 ? '' : 's'} free in your qualifying group${freeBeds === 1 ? '' : 's'} of four.`;
    }
    if (campaign.type === 'spend-threshold') {
      const eligibleTotal = cart.items.reduce((total, item) => total + (item.gift_card ? 0 : (item.original_line_price || 0)), 0);
      if (eligibleTotal < campaign.minimumSpendCents) {
        const remaining = (campaign.minimumSpendCents - eligibleTotal) / 100;
        return `Add $${remaining.toFixed(2)} more to unlock $100 off your order.`;
      }
      return codeApplicable ? '$100 off your order is ready at checkout.' : 'Your cart qualifies, but the $100 discount is not applying. Check your cart before checkout.';
    }
    if (campaign.type === 'second-bed') {
      if (bedCount < 1) return 'Add a raised bed to get started.';
      if (bedCount < 2) return 'Add one more raised bed to unlock 50% off the lower-priced bed.';
      const pairCount = Math.floor(bedCount / 2);
      const pairs = `${pairCount} qualifying pair${pairCount === 1 ? ' is' : 's are'}`;
      if (!codeApplicable) return `${pairs} in your cart, but this offer cannot combine with your current discount. Check your cart before checkout.`;
      if (bedCount % 2) return `${pairs} eligible for 50% off the lower-priced bed. Add one more raised bed to complete another pair.`;
      return `${pairs} eligible for 50% off the lower-priced bed in each pair.`;
    }
    if (bedCount < 1) return `Your free ${campaign.giftTitle} is waiting. Add any raised bed.`;
    return codeApplicable ? `${campaign.giftTitle} added to your cart` : `Your raised bed qualifies, but the free bundle is not available with your current discount. Check your cart before checkout.`;
  };

  const updateBarHeight = () => {
    const bar = document.querySelector('#eg-bed-offer-bar');
    if (!bar) return;
    document.body.style.setProperty('--eg-bed-offer-bar-height', `${bar.offsetHeight}px`);
  };

  const render = (campaign, bedCount, codeApplicable, cart) => {
    const activeOfferPage = roots().some((root) => (
      !root.hidden && root.dataset.campaignId === getState()?.campaignId
    ));
    if (!activeOfferPage) {
      document.querySelector('#eg-bed-offer-bar')?.remove();
      document.body.classList.remove('eg-bed-offer-active');
      document.body.style.removeProperty('--eg-bed-offer-bar-height');
      return;
    }

    const message = messageFor(campaign, bedCount, codeApplicable, cart);
    if (!document.querySelector('link[href*="raised-bed-email-offers.css"]')) {
      const styles = document.createElement('link');
      styles.rel = 'stylesheet';
      styles.href = script.dataset.styles;
      styles.addEventListener('load', updateBarHeight, { once: true });
      document.head.append(styles);
    }
    roots().forEach((root) => {
      if (root.dataset.campaignId !== getState()?.campaignId) return;
      root.querySelector('[data-raised-bed-email-offer-status]').textContent = '';
    });

    let bar = document.querySelector('#eg-bed-offer-bar');
    if (!bar) {
      bar = document.createElement('aside');
      bar.id = 'eg-bed-offer-bar';
      bar.setAttribute('aria-label', 'Offer progress');
      bar.innerHTML = '<div class="eg-bed-offer-bar__inner"><div class="eg-bed-offer-bar__copy" role="status"><strong>Your raised-bed offer is active</strong><span></span></div><a class="button eg-bed-offer-bar__link" href="/cart">View cart</a></div>';
      document.body.append(bar);
    }
    bar.dataset.campaignId = getState()?.campaignId || '';
    document.body.classList.add('eg-bed-offer-active');
    bar.querySelector('strong').textContent = campaign.type === 'spend-threshold' ? 'Spend $400, save $100' : 'Your raised-bed offer is active';
    bar.querySelector('span').textContent = message;
    bar.querySelector('.eg-bed-offer-bar__link').hidden = campaign.type === 'gift' && bedCount < 1;
    updateBarHeight();
  };

  const sync = async () => {
    await getData();
    let cart = await api('cart.js');
    const state = recoverState(cart);
    const campaign = getCampaign();
    if (!state || !campaign) {
      const orphanedGifts = cart.items.filter((item) => item.properties?.[giftProperty]);
      if (orphanedGifts.length) {
        cart = await updateGiftItems(orphanedGifts, false);
        publishCartUpdate(cart);
      }
      await reconcileCartDrawer(cart);
      return;
    }

    if (state.codeSeen && !hasCampaignCode(cart, campaign)) {
      const abandonedGifts = cart.items.filter((item) => item.properties?.[giftProperty]);
      if (abandonedGifts.length) {
        cart = await updateGiftItems(abandonedGifts, false);
        publishCartUpdate(cart);
      }
      clearState();
      await reconcileCartDrawer(cart);
      return;
    }

    const qualifyingIds = campaign.qualifyingCollectionHandle ? await getQualifyingProductIds(campaign) : new Set();
    const bedCount = qualifyingBedCount(cart, qualifyingIds);
    const giftFingerprint = cart.items
      .filter((item) => qualifyingIds.has(String(item.product_id)))
      .map((item) => `${item.key}:${item.quantity}`)
      .sort()
      .join('|') + `:${state.campaignId}`;
    let changed = false;

    const otherCampaignGifts = cart.items.filter((item) => (
      item.properties?.[giftProperty] && item.properties[giftProperty] !== state.campaignId
    ));
    if (otherCampaignGifts.length) {
      cart = await updateGiftItems(otherCampaignGifts, false);
      changed = true;
    }

    if (campaign.type === 'gift') {
      const gifts = giftItems(cart, state.campaignId);
      if (!bedCount && gifts.length) {
        cart = await updateGiftItems(gifts, false);
        changed = true;
      } else if (bedCount && gifts.length > 0 && (gifts.length > 1 || gifts[0].quantity !== 1)) {
        cart = await updateGiftItems(gifts, true);
        changed = true;
      } else if (bedCount && !gifts.length && giftFingerprint !== rejectedGiftFingerprint) {
        const product = await api(`products/${campaign.giftHandle}.js`);
        const variant = product.variants.find((item) => item.id === campaign.giftVariantId && item.available);
        if (!variant) throw new Error(`The ${campaign.giftTitle} is currently unavailable. Please try again later.`);
        cart = await api('cart/add.js', {
          items: [{
            id: campaign.giftVariantId,
            quantity: 1,
            properties: { [giftProperty]: state.campaignId },
          }],
        });
        changed = true;
      }
    }

    if (!state.codeSeen && !hasCampaignCode(cart, campaign)) {
      cart = await applyCampaignCode(campaign);
      changed = true;
    } else if (changed) {
      cart = await api('cart.js');
    }
    if (hasCampaignCode(cart, campaign)) setState(state.campaignId, true);
    const codeApplicable = offerCodeIsApplicable(cart, campaign);

    if (campaign.type === 'gift') {
      const gifts = giftItems(cart, state.campaignId);
      const giftIsFree = gifts.length === 1 && gifts[0].final_line_price === 0;
      if (!codeApplicable || (bedCount > 0 && gifts.length && !giftIsFree)) {
        if (gifts.length) {
          rejectedGiftFingerprint = giftFingerprint;
          cart = await updateGiftItems(gifts, false);
          changed = true;
        }
      } else if (giftIsFree) {
        rejectedGiftFingerprint = '';
      }
    }

    const giftIsPresentAndFree = campaign.type === 'gift' && giftItems(cart, state.campaignId).some((gift) => gift.final_line_price === 0);
    render(campaign, bedCount, campaign.type === 'gift' ? giftIsPresentAndFree : codeApplicable, cart);
    if (changed) publishCartUpdate(cart);
    await reconcileCartDrawer(cart);
  };

  const enqueueSync = () => {
    const task = syncQueue.then(sync);
    syncQueue = task.catch(() => {});
    return task;
  };

  const scheduleSync = () => {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(() => {
      enqueueSync().catch((error) => {
        const barMessage = document.querySelector('#eg-bed-offer-bar .eg-bed-offer-bar__copy span');
        if (barMessage) barMessage.textContent = error.message;
        roots().forEach((root) => {
          root.querySelector('[data-raised-bed-email-offer-status]').textContent = error.message;
        });
      });
    }, 125);
  };

  const showCheckoutError = (error) => {
    const message = error.message || 'We could not confirm your offer. Please try again.';
    const barMessage = document.querySelector('#eg-bed-offer-bar .eg-bed-offer-bar__copy span');
    if (barMessage) barMessage.textContent = message;
    roots().forEach((root) => {
      root.querySelector('[data-raised-bed-email-offer-status]').textContent = message;
    });
  };

  const initializeLandingPages = async () => {
    await getData();
    roots().forEach((root) => {
      const parameter = root.dataset.requiredUrlParameter;
      const value = root.dataset.requiredUrlValue;
      const permitted = !parameter || !value || new URLSearchParams(window.location.search).get(parameter) === value;
      const restricted = document.querySelector(`[data-raised-bed-email-offer-restricted-for="${root.id}"]`);
      if (!permitted) {
        root.hidden = true;
        if (restricted) restricted.hidden = false;
        return;
      }
      root.hidden = false;
      if (data.campaigns[root.dataset.campaignId]) setState(root.dataset.campaignId);
    });
  };

  // The creator offer has its own code flow. Never reapply an email code there.
  if (document.querySelector('[data-raised-bed-creator-offer]')) {
    clearState();
    return;
  }

  if (!roots().length && !getState() && !/\/cart\/?$/.test(location.pathname)) return;

  window.addEventListener('resize', updateBarHeight);

  document.addEventListener('cart:updated', (event) => {
    if (event.detail?.source !== 'raised-bed-email-offer') scheduleSync();
  });

  document.addEventListener('cart-drawer:updated', (event) => {
    if (event.detail?.source !== 'raised-bed-email-offer') scheduleSync();
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source !== 'raised-bed-email-offer') scheduleSync();
    });
  }

  // Cart rendering can fail after a successful removal, before it publishes cart:updated.
  // Reconcile the gift once the removal request settles so the drawer cannot retain it.
  document.addEventListener('click', (event) => {
    if (!getState() || !event.target.closest?.('cart-remove-button')) return;

    const pending = window.cartState?.pendingOperations;
    if (pending?.size) {
      Promise.allSettled([...pending]).then(scheduleSync);
    } else {
      setTimeout(scheduleSync, 1000);
    }
  });

  document.addEventListener('click', (event) => {
    const checkout = event.target.closest?.('[name="checkout"], [data-cart-page-checkout], a[href^="/checkout"]');
    if (!checkout || !getState()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    enqueueSync().then(() => { window.location.assign('/checkout'); }).catch(showCheckoutError);
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !getState() || !new URL(form.action, location.href).pathname.match(/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?cart\/?$/i) || !event.submitter?.matches('[name="checkout"], [data-cart-page-checkout]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    enqueueSync().then(() => { window.location.assign('/checkout'); }).catch(showCheckoutError);
  }, true);

  initializeLandingPages().then(() => {
    if (getState()) scheduleSync();
  }).catch(() => {});
})();
